import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { Dictionary, KeyValuePair } from "../Utils/Dictionary";
import { Iterable } from "../Utils/ObjUtils";
import { Guid } from "../Utils/Guid";
import { List } from "../Utils/List";
import { MonitorPolicy } from "./MonitorPolicy";
import { MonitorEntry } from "../InfoSets/MonitorTable";
import { Device } from "../Core/Device";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { HashSet } from "../Utils";


export class DeviceMonitorMap implements Iterable<MonitorEntry>, ObjectSerializer {

	public MonitorPolicy: MonitorPolicy;

	// DEVICE to MonitorEntry Map
	private _deviceMonitorMap: Dictionary<Guid, MonitorEntry>;

	constructor(monPolicy: MonitorPolicy) {
		this.MonitorPolicy = monPolicy;
		this._deviceMonitorMap = new Dictionary<Guid, MonitorEntry>();
	}

	public get DeviceGuids(): HashSet<Guid> {
		return new HashSet<Guid>(this._deviceMonitorMap.Keys());
	}

	public Clear(): void {
		this._deviceMonitorMap.Clear();
	}

	public get Count(): number {
		return this._deviceMonitorMap.Count;
	}

	public ForEach(callbackfn: (val: MonitorEntry, index: number, arr: MonitorEntry[]) => void, thisArg?: any): void {
		this._deviceMonitorMap.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (val: MonitorEntry, index: number, arr: MonitorEntry[]) => boolean, thisArg?: any): boolean {
		return this._deviceMonitorMap.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (val: MonitorEntry, index: number, arr: MonitorEntry[]) => boolean, thisArg?: any): boolean {
		return this._deviceMonitorMap.Values().Every(callbackfn, thisArg);
	}

	public Copy(other: DeviceMonitorMap, copyCollectedValues: boolean = true): void {
		if (!other)
			return;

		// Do not copy MonitorPolicy because doing so would transfer ownership of this DeviceMonitorMap
		// to a different MonitorPolicy, which is not what we want.
		//// NOTE: The MonitorPolicy is only a reference (we don't need to do a deep copy)
		//this.MonitorPolicy = other.MonitorPolicy;

		// COPY the Monitor map
		this._deviceMonitorMap.Clear();

		other._deviceMonitorMap.ForEach((otherKvp: KeyValuePair<Guid, MonitorEntry>) => {

			if (!!otherKvp.Key && !otherKvp.Key.Equals(Guid.Empty) &&
				!this._deviceMonitorMap.ContainsKey(otherKvp.Key)) {

				// CREATE a COPY of the MonitorEntry (with/without collected stats)
				let copyMonitorEntry = new MonitorEntry();
				if (copyCollectedValues)
					copyMonitorEntry.Copy(otherKvp.Value);
				else
					copyMonitorEntry.CopyConfig_MonitorEntry(otherKvp.Value);

				this._deviceMonitorMap.Add(otherKvp.Key, copyMonitorEntry);
			}
		}, this);
	}

	public Add(deviceGuid: Guid, monitorEntry: MonitorEntry): void {
		console.assert(!!monitorEntry);

		this._deviceMonitorMap.Add(deviceGuid, monitorEntry);
	}

	public AddDevice(device: Device): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;
		if (!device.HasGUID)
			return false;

		let result = false;

		if (!this._deviceMonitorMap.ContainsKey(device.GUID)) {
			result = true;
			this.AddUpdateMonitor(device);
		}

		return result;
	}

	public HasDevice_Guid(deviceGuid: Guid): boolean {
		return this._deviceMonitorMap.ContainsKey(deviceGuid);
	}

	public HasDevice_Device(device: Device): boolean {
		if (!device)
			return false;
		if (!device.HasGUID)
			return false;

		return this._deviceMonitorMap.ContainsKey(device.GUID);
	}

	public RemoveDevice(device: Device): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;
		if (!device.HasGUID)
			return false;

		return this.RemoveDevice_Guid(device.GUID);
	}

	public RemoveDevice_Guid(deviceGuid: Guid): boolean {
		console.assert(!!deviceGuid);

		// REMOVE the MonitorTable Map
		return this._deviceMonitorMap.Remove(deviceGuid);
	}

	public AddMonitor(device: Device): MonitorEntry {
		console.assert(MonitorPolicy != null);
		console.assert(device != null);
		console.assert(device.HasGUID);
		if (!device)
			return null;

		let monitorEntry: MonitorEntry = null;

		if (this._deviceMonitorMap.ContainsKey(device.GUID)) {
			monitorEntry = this._deviceMonitorMap.Item(device.GUID);
		}
		else {
			monitorEntry = MonitorEntry.Create_MonitorPolicy(this.MonitorPolicy, device);

			this._deviceMonitorMap.Add(device.GUID, monitorEntry);
		}

		return monitorEntry;
	}

	public AddUpdateMonitor(device: Device): MonitorEntry {
		console.assert(!!this.MonitorPolicy);
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return null;

		let monitorEntry: MonitorEntry = null;

		if (this._deviceMonitorMap.ContainsKey(device.GUID)) {
			monitorEntry = this._deviceMonitorMap.Item(device.GUID);

			// UPDATE the POLICY settings for this specific Monitor
			monitorEntry.CopyConfig_MonitorPolicy(this.MonitorPolicy, device.GUID);
		}
		else {
			monitorEntry = MonitorEntry.Create_MonitorPolicy(this.MonitorPolicy, device);

			this._deviceMonitorMap.Add(device.GUID, monitorEntry);
		}

		return monitorEntry;
	}

	public GetMonitor_Device(device: Device): MonitorEntry {
		console.assert(device.HasGUID);
		return this.GetMonitor_DeviceGuid(device.GUID);
	}

	public GetMonitor_DeviceGuid(deviceGuid: Guid): MonitorEntry {
		let monitorEntry: MonitorEntry = null;

		if (this._deviceMonitorMap.ContainsKey(deviceGuid)) {
			monitorEntry = this._deviceMonitorMap.Item(deviceGuid);
		}

		return monitorEntry;
	}

	public get HasGuids(): boolean {

		// CHECK to ensure that ALL entries have a GUID assigned

		let monitorEntries: List<MonitorEntry> = this._deviceMonitorMap.Values();
		for (let i = 0; i < monitorEntries.Count; ++i) {

			let monitorEntry: MonitorEntry = monitorEntries.Get(i);

			if (!monitorEntry.HasSettings)
				continue;

			if (monitorEntry.Settings.HasGuids)
				continue;

			return false;
		}

		return true;
	}

	public VerifyGuids(): boolean {
		let guidsAdded = false;

		if (this._deviceMonitorMap.Count > 0) {
			// CHECK to ensure that ALL entries have a GUID assigned
			this._deviceMonitorMap.ForEach((keyVP: KeyValuePair<Guid, MonitorEntry>) => {
				let monitorEntry: MonitorEntry = keyVP.Value;

				if (!!monitorEntry && monitorEntry.HasSettings) {
					if (monitorEntry.Settings.VerifyGuids()) {
						guidsAdded = true;
					}

					if (this.MonitorPolicy && this.MonitorPolicy.HasMonitorPolicyId) {
						let policyGuid: Guid = this.MonitorPolicy.MonitorPolicyId;
						let deviceGuid: Guid = keyVP.Key;

						monitorEntry.Settings.ApplyParentGuids(policyGuid, deviceGuid);
						guidsAdded = true;
					}
				}
			}, this);
		}

		return guidsAdded;
	}

	public Write(obj: any, context: any): void {
		obj.device_monitor_map = {};
		obj = obj.device_monitor_map;

		if (this.Count > 0) {
			obj.entries = [];
			ObjSerUtils.WriteCollection(obj.entries, this._deviceMonitorMap,
				(value: KeyValuePair<Guid, MonitorEntry>): any => {
					let elem: any = {
						entry: {}
					};

					elem.entry.guid = ValueEncoder.EncodeGuid(value.Key);
					value.Value.Write(elem.entry, context);

					return elem;
				}, context);
		}
	}

	public Read(obj: any, context: any): void {

		this.Clear();

		if (!obj.device_monitor_map)
			throw new Error();
		obj = obj.device_monitor_map;

		if (obj.entries !== undefined) {
			obj.entries.forEach((elem: any) => {
				if (!!elem && !!elem.entry) {
					let entry = elem.entry;

					let guid: Guid = null;
					let monitorEntry: MonitorEntry = null;

					if (entry.guid !== undefined) {
						guid = ValueEncoder.DecodeGuid(entry.guid);
					}

					if (entry.monitor_entry !== undefined) {
						monitorEntry = new MonitorEntry();
						monitorEntry.Read(entry, context);
					}

					if (!!guid && !!monitorEntry) {
						this._deviceMonitorMap.Add(guid, monitorEntry);
					}
				}
			}, this);
		}
	}
}
